// Update the existing dashboard-page.jsx
import React, { useState, useEffect } from "react";
import { Sidebar } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/ui/dashboard/dashboard-header";
import { HeroBanner } from "@/components/ui/dashboard/hero-banner";
import { DashboardStatsGrid } from "@/components/ui/dashboard/dashboard-stats-grid";
import { RecommendedCareerPaths } from "@/components/ui/dashboard/recommended-career-paths";
import { ProfileCard } from "@/components/ui/profile/profile-card";
import { GradesChart } from "@/components/ui/dashboard/grades-chart";
import { AchievementsGrid } from "@/components/ui/dashboard/achievements-card";
import { UniversalSearch } from "@/components/ui/universal-search";
import { NotesEditorModal } from "@/components/ui/curriculum/notes-editor-modal";
import { CareerSurveyModal } from "@/components/ui/dashboard/career-survey-modal";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useUserData } from "@/hooks/useUserData";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { useThemeStore } from "@/hooks/stores/themeStore";
import { AIChatBox } from "@/components/ui/ai-chat-box";

export default function DashboardPage() {
  const { user, goals, achievements, weeklyProgress, academicPerformance, careerPaths, isLoading } =
    useUserData();
    
  // Get hydration state from AuthStore
  // const hasHydrated = AuthStore((state) => state._hasHydrated);
  
  const [isNotesEditorOpen, setIsNotesEditorOpen] = useState(false);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  
  const currentTheme = useThemeStore((state) => state.getCurrentTheme());

  // Handle Survey Logic
  useEffect(() => {
    // Only run this logic once the store has loaded (hydrated)
    // if (hasHydrated) {
    if(!isLoading) {
      const hasCompletedSurvey = localStorage.getItem("careerSurveyCompleted");
      const userCareerPath = localStorage.getItem("userCareerPath");
      
      if (!hasCompletedSurvey && !userCareerPath) {
        setTimeout(() => setIsSurveyModalOpen(true), 1500);
        // const timer = setTimeout(() => {
        //   setIsSurveyModalOpen(true);
        // }, 1500);
        // return () => clearTimeout(timer);
      }
    }
  }, [isLoading]); // Depend on hydration

  const handleNotesClick = () => {
    setIsNotesEditorOpen(true);
  };

  const handleSearch = (query) => {
    console.log("Universal search for:", query);
  };

  const handleSurveyComplete = () => {
    // Mark survey as completed
    localStorage.setItem("careerSurveyCompleted", "true");
    setIsSurveyModalOpen(false);
  };

  // Show loading state if store isn't ready or data is fetching
  // if (!hasHydrated || dataLoading) {
  if(isLoading) {
    return (
      <ProtectedRoute>
        <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: 'var(--theme-background)' }}>
          <div className="text-center">
             <div 
              className="w-16 h-16 border-4 rounded-full animate-spin mx-auto mb-4"
              style={{ borderColor: currentTheme.colors.primary, borderTopColor: 'transparent' }}
            ></div>
            <p className="text-lg" style={{ color: currentTheme.colors.foreground }}>
              Loading your dashboard...
            </p>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div 
        className="flex min-h-screen"
        style={{ backgroundColor: 'var(--theme-background)' }}
      >
        {/* Sidebar */}
        <Sidebar />

        {/* Universal Search Component */}
        <UniversalSearch onSearch={handleSearch} />

        <AIChatBox />

        {/* Main Content */}
        <main 
          className="flex-1 lg:ml-64 p-3 sm:p-4 md:p-6 lg:p-8 overflow-y-auto w-full transition-all duration-300 ease-in-out"
          style={{ backgroundColor: 'var(--theme-background)' }}
        >
          {/* Mobile Header with Menu Button */}
          <div 
            className="md:hidden flex items-center gap-3 mb-4 pb-4 border-b"
            style={{ borderColor: 'var(--theme-border)' }}
          >
            <div className="flex-1">
              <h1 
                className="text-lg font-bold"
                style={{ color: 'var(--theme-foreground)' }}
              >
                Dashboard
              </h1>
              <p 
                className="text-sm"
                style={{ color: 'var(--theme-foreground)', opacity: 0.7 }}
              >
                Welcome back, {user.name}
              </p>
            </div>
          </div>

          {/* Desktop Dashboard Header */}
          <div className="hidden md:block">
            <DashboardHeader 
              userName={user.name} 
              userImage={user.avatarUrl} 
            />
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6 md:space-y-8">
                {/* Hero Banner */}
                <div className="overflow-x-auto">
                  <HeroBanner 
                    userName={user.name} 
                    themeColors={currentTheme.colors}
                  />
                </div>

                {/* Stats Grid */}
                <div className="overflow-x-auto">
                  <DashboardStatsGrid
                    totalPoints={user.totalPoints}
                    tasksCompleted={user.tasksCompleted}
                    streakDays={user.streakDays}
                    level={user.level}
                    themeColors={currentTheme.colors}
                  />
                </div>

                {/* Recommended Career Paths */}
                <div className="overflow-x-auto">
                  <RecommendedCareerPaths 
                    careerPaths={careerPaths}
                    themeColors={currentTheme.colors}
                  />
                </div>
              </div>

              {/* Right Column - Sidebar Content */}
              <div className="lg:col-span-1 space-y-4 sm:space-y-6 md:space-y-8">
                {/* Profile Card */}
                <div className="overflow-x-auto">
                  <ProfileCard 
                    user={user} 
                    goals={goals}
                    themeColors={currentTheme.colors}
                  />
                </div>

                {/* Grades Chart */}
                <div className="overflow-x-auto">
                  <GradesChart 
                    academicPerformance={academicPerformance}
                    themeColors={currentTheme.colors}
                  />
                </div>

                {/* Achievements Grid */}
                <div className="overflow-x-auto">
                  <AchievementsGrid 
                    achievements={achievements}
                    themeColors={currentTheme.colors}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Notes Editor Modal */}
        {isNotesEditorOpen && (
          <NotesEditorModal
            isOpen={isNotesEditorOpen}
            onClose={() => setIsNotesEditorOpen(false)}
            themeColors={currentTheme.colors}
          />
        )}

        {/* Career Survey Modal */}
        <CareerSurveyModal
          isOpen={isSurveyModalOpen}
          onClose={handleSurveyComplete}
          themeColors={currentTheme.colors}
        />
      </div>
    </ProtectedRoute>
  );
}